import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, KeyRound, Copy } from "lucide-react";
import { useI18n, type TFunction } from "@faceheart/i18n";
import { alertsApi, sitesApi, adminUsersApi } from "../lib/api/endpoints";
import { ApiError } from "../lib/api/client";
import { useAuth } from "../lib/auth/AuthProvider";
import type {
  AlertRule,
  AlertRuleInput,
  AlertRuleView,
  Site,
  AdminUser,
} from "@faceheart/api-types";

type Tab = "sites" | "users" | "rules" | "notifications" | "about";

const ALL_TABS: Tab[] = ["sites", "users", "rules", "notifications", "about"];

export function SettingsPage() {
  const { t } = useI18n("admin");
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>("rules");

  // The Users tab is admin-only (SPEC-012 §8). Backend enforces 403 too; this
  // is defense-in-depth + UX so site_admins never see a tab they can't use.
  const tabs = ALL_TABS.filter((v) => v !== "users" || user?.role === "admin");

  // ADR-027: a site_admin may VIEW settings but not edit them (sites /
  // alert-rules writes all 403 server-side). Render the editable tabs read-only
  // so the controls match what the backend will accept.
  const isReadOnly = user?.role !== "admin";

  return (
    <div className="p-8">
      <h1 className="text-xl font-medium mb-6">{t("settings.title")}</h1>

      <div className="flex border-b border-admin-border mb-6">
        {tabs.map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => setTab(value)}
            className={`px-4 py-2 text-sm transition-colors border-b-2 -mb-px ${
              tab === value
                ? "border-admin-cyan text-admin-cyan"
                : "border-transparent text-admin-text2 hover:text-admin-text1"
            }`}
          >
            {t(`settings.tabs.${value}`)}
          </button>
        ))}
      </div>

      {tab === "rules" && <AlertRulesTab t={t} readOnly={isReadOnly} />}
      {tab === "sites" && <SitesTab t={t} readOnly={isReadOnly} />}
      {tab === "users" && <UsersTab t={t} />}
      {tab === "notifications" && <NotificationsTab t={t} />}
      {tab === "about" && <AboutTab t={t} />}
    </div>
  );
}

// ─── Sites tab (SPEC-011) ────────────────────────────────────────────────
// Shown at the top of an editable settings tab when the viewer is a site_admin:
// they can read the configuration but only HQ admins can change it (ADR-027).
function ReadOnlyHint({ t }: { t: TFunction }) {
  return (
    <div className="px-4 py-2.5 border-b border-admin-border bg-admin-bg3/40 text-xs text-admin-text3">
      {t("settings.readOnlyHint")}
    </div>
  );
}

function SitesTab({ t, readOnly }: { t: TFunction; readOnly: boolean }) {
  const qc = useQueryClient();
  const listQ = useQuery({ queryKey: ["sites"], queryFn: () => sitesApi.list() });
  const [newName, setNewName] = useState("");

  const invalidate = () => void qc.invalidateQueries({ queryKey: ["sites"] });

  const createMut = useMutation({
    mutationFn: (name: string) => sitesApi.create(name),
    onSuccess: () => {
      setNewName("");
      invalidate();
      toast.success(t("settings.sites.created"));
    },
    onError: (err) => toast.error(siteError(t, err)),
  });
  const removeMut = useMutation({
    mutationFn: (id: string) => sitesApi.remove(id),
    onSuccess: () => {
      invalidate();
      toast.success(t("settings.sites.deleted"));
    },
    onError: (err) => toast.error(siteError(t, err)),
  });

  const sites = listQ.data ?? [];

  return (
    <div className="bg-admin-bg2 border border-admin-border rounded-lg">
      {readOnly ? (
        <ReadOnlyHint t={t} />
      ) : (
        <div className="px-4 py-3 border-b border-admin-border flex items-center gap-2">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && newName.trim()) createMut.mutate(newName.trim());
            }}
            placeholder={t("settings.sites.namePlaceholder")}
            maxLength={50}
            className="flex-1 bg-admin-bg3 border border-admin-border rounded px-3 py-1.5 text-sm text-admin-text1 focus:outline-none focus:border-admin-cyan"
          />
          <button
            type="button"
            disabled={!newName.trim() || createMut.isPending}
            onClick={() => createMut.mutate(newName.trim())}
            className="inline-flex items-center gap-1.5 text-sm text-admin-cyan hover:underline disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4" /> {t("settings.sites.add")}
          </button>
        </div>
      )}

      {listQ.isLoading ? (
        <div className="p-6 text-admin-text2 text-sm">{t("common:actions.loading")}</div>
      ) : sites.length === 0 ? (
        <div className="p-8 text-center text-admin-text3 text-sm">
          {t("settings.sites.empty")}
        </div>
      ) : (
        <ul className="divide-y divide-admin-border">
          {sites.map((s: Site) => (
            <li key={s.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
              <span className="text-admin-text1">{s.name}</span>
              <div className="flex items-center gap-4">
                <span className="text-admin-text3 text-xs">
                  {t("settings.sites.peopleCount", { count: s.employee_count })}
                </span>
                {!readOnly && (
                  <button
                    type="button"
                    disabled={s.employee_count > 0 || removeMut.isPending}
                    onClick={() => removeMut.mutate(s.id)}
                    title={
                      s.employee_count > 0 ? t("settings.sites.cannotDeleteHint") : undefined
                    }
                    className="inline-flex items-center text-admin-red hover:text-admin-red/80 disabled:opacity-30 disabled:cursor-not-allowed"
                    aria-label={t("settings.sites.deleteAria")}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Map a site API error code to a localised toast message.
function siteError(t: TFunction, err: unknown): string {
  const code = err instanceof ApiError ? err.code : "internal";
  switch (code) {
    case "already_exists":
      return t("settings.sites.errDuplicate");
    case "site_not_empty":
    case "site_in_use":
      return t("settings.sites.errNotEmpty");
    default:
      return t("settings.sites.errGeneric");
  }
}

// ─── Users tab (SPEC-012) ────────────────────────────────────────────────
function UsersTab({ t }: { t: TFunction }) {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const listQ = useQuery({ queryKey: ["admin-users"], queryFn: () => adminUsersApi.list() });
  // A site_admin must be bound to a site at creation (ADR-027 / AdminUserCreateRequest
  // requires site_id when role==="site_admin"). The picker below appears only for
  // that role; an `admin` account omits site_id.
  const sitesQ = useQuery({ queryKey: ["sites"], queryFn: () => sitesApi.list() });

  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"admin" | "site_admin">("site_admin");
  const [siteId, setSiteId] = useState("");
  const [password, setPassword] = useState("");
  // The one-time plaintext from a reset / create, shown in a copy-once banner
  // (never persisted — SPEC-012 §7).
  const [tempPw, setTempPw] = useState<{ email: string; pw: string } | null>(null);

  const invalidate = () => void qc.invalidateQueries({ queryKey: ["admin-users"] });

  const createMut = useMutation({
    mutationFn: () =>
      adminUsersApi.create({
        email: email.trim(),
        role,
        password,
        // site_id only applies to site_admin; admin must omit it.
        ...(role === "site_admin" ? { site_id: siteId } : {}),
      }),
    onSuccess: () => {
      setEmail("");
      setPassword("");
      setRole("site_admin");
      setSiteId("");
      invalidate();
      toast.success(t("settings.users.created"));
    },
    onError: (err) => toast.error(userError(t, err)),
  });
  const patchMut = useMutation({
    mutationFn: (v: { id: string; body: Parameters<typeof adminUsersApi.patch>[1] }) =>
      adminUsersApi.patch(v.id, v.body),
    onSuccess: () => {
      invalidate();
      toast.success(t("settings.users.updated"));
    },
    onError: (err) => toast.error(userError(t, err)),
  });
  const resetMut = useMutation({
    mutationFn: (u: AdminUser) => adminUsersApi.passwordReset(u.id),
    onSuccess: (resp, u) => setTempPw({ email: u.email, pw: resp.temporary_password }),
    onError: (err) => toast.error(userError(t, err)),
  });

  const users = listQ.data ?? [];
  const sites = sitesQ.data ?? [];
  const canCreate =
    email.trim() !== "" &&
    password.length >= 10 &&
    (role !== "site_admin" || siteId !== "") &&
    !createMut.isPending;

  return (
    <div className="space-y-4">
      {tempPw && (
        <div className="bg-admin-bg2 border border-admin-cyan/40 rounded-lg p-4 text-sm">
          <div className="text-admin-text2 mb-2">
            {t("settings.users.tempPwFor", { email: tempPw.email })}
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-admin-bg3 border border-admin-border rounded px-3 py-1.5 font-mono text-admin-cyan break-all">
              {tempPw.pw}
            </code>
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard?.writeText(tempPw.pw);
                toast.success(t("settings.users.copied"));
              }}
              className="inline-flex items-center gap-1 text-admin-cyan hover:underline"
            >
              <Copy className="w-4 h-4" /> {t("common:actions.copy")}
            </button>
            <button
              type="button"
              onClick={() => setTempPw(null)}
              className="text-admin-text3 hover:text-admin-text1"
            >
              {t("common:actions.dismiss")}
            </button>
          </div>
          <p className="text-admin-text3 text-xs mt-2">{t("settings.users.tempPwHint")}</p>
        </div>
      )}

      {/* create row */}
      <div className="bg-admin-bg2 border border-admin-border rounded-lg px-4 py-3 flex flex-wrap items-center gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t("settings.users.emailPlaceholder")}
          className="flex-1 min-w-45 bg-admin-bg3 border border-admin-border rounded px-3 py-1.5 text-sm text-admin-text1 focus:outline-none focus:border-admin-cyan"
        />
        <Select
          value={role}
          onChange={(v) => setRole(v as "admin" | "site_admin")}
          options={["site_admin", "admin"]}
          renderLabel={(v) => t(`settings.users.roles.${v}`)}
        />
        {role === "site_admin" && (
          <select
            value={siteId}
            onChange={(e) => setSiteId(e.target.value)}
            className="bg-admin-bg3 border border-admin-border rounded px-2 py-1.5 text-sm text-admin-text1 focus:outline-none focus:border-admin-cyan"
          >
            <option value="">{t("settings.users.sitePlaceholder")}</option>
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        )}
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={t("settings.users.passwordPlaceholder")}
          className="min-w-40 bg-admin-bg3 border border-admin-border rounded px-3 py-1.5 text-sm text-admin-text1 focus:outline-none focus:border-admin-cyan"
        />
        <button
          type="button"
          disabled={!canCreate}
          onClick={() => createMut.mutate()}
          className="inline-flex items-center gap-1.5 text-sm text-admin-cyan hover:underline disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Plus className="w-4 h-4" /> {t("settings.users.add")}
        </button>
      </div>

      {/* list */}
      <div className="bg-admin-bg2 border border-admin-border rounded-lg">
        {listQ.isLoading ? (
          <div className="p-6 text-admin-text2 text-sm">{t("common:actions.loading")}</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-admin-bg3 text-admin-text2 text-xs">
              <tr>
                <Th>{t("settings.users.headers.email")}</Th>
                <Th>{t("settings.users.headers.role")}</Th>
                <Th>{t("settings.users.headers.status")}</Th>
                <Th> </Th>
              </tr>
            </thead>
            <tbody>
              {users.map((u: AdminUser) => {
                const isSelf = u.id === me?.id;
                return (
                  <tr key={u.id} className="border-t border-admin-border">
                    <td className="px-3 py-2 text-admin-text1">
                      {u.email}
                      {isSelf && (
                        <span className="text-admin-text3 text-xs ml-2">
                          {t("settings.users.selfTag")}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <Select
                        value={u.role}
                        onChange={(v) => patchMut.mutate({ id: u.id, body: { role: v as "admin" | "site_admin" } })}
                        options={["site_admin", "admin"]}
                        renderLabel={(v) => t(`settings.users.roles.${v}`)}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() => patchMut.mutate({ id: u.id, body: { is_active: !u.is_active } })}
                        className={`text-xs px-2 py-0.5 rounded ${
                          u.is_active
                            ? "bg-admin-green/20 text-admin-green"
                            : "bg-admin-red/20 text-admin-red"
                        }`}
                      >
                        {u.is_active ? t("settings.users.active") : t("settings.users.inactive")}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => resetMut.mutate(u)}
                        className="inline-flex items-center gap-1 text-admin-text2 hover:text-admin-cyan text-xs"
                      >
                        <KeyRound className="w-3.5 h-3.5" /> {t("settings.users.resetPw")}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// Map an admin-user API error code to a localised toast message.
function userError(t: TFunction, err: unknown): string {
  const code = err instanceof ApiError ? err.code : "internal";
  switch (code) {
    case "already_exists":
      return t("settings.users.errDuplicate");
    case "weak_password":
      return t("settings.users.errWeakPassword");
    case "cannot_modify_self":
      return t("settings.users.errSelf");
    case "last_admin":
      return t("settings.users.errLastAdmin");
    default:
      return t("settings.users.errGeneric");
  }
}

// ─── Notifications tab ───────────────────────────────────────────────────
function NotificationsTab({ t }: { t: TFunction }) {
  // Only render channels that are actually live. Disabled channels are kept
  // out of the UI until they're wired up — surfacing them as "未啟用" rows
  // just adds noise without giving the operator anything to do.
  const channels: Array<{ key: "email" | "slack" | "sse" | "sms" }> = [
    { key: "sse" },
  ];
  return (
    <div className="bg-admin-bg2 border border-admin-border rounded-lg p-6 text-sm">
      <h2 className="text-base font-medium mb-3">{t("settings.notifications.title")}</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {channels.map((c) => (
          <div key={c.key} className="border border-admin-border rounded p-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-admin-text1">
                {t(`settings.notifications.channels.${c.key}`)}
              </span>
              <span className="text-xs px-2 py-0.5 rounded bg-admin-green/20 text-admin-green">
                {t("settings.notifications.enabled")}
              </span>
            </div>
            <p className="text-xs text-admin-text3">
              {t(`settings.notifications.reasons.${c.key}`)}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── About tab ───────────────────────────────────────────────────────────
function AboutTab({ t }: { t: TFunction }) {
  const { data: health } = useQuery({
    queryKey: ["healthz"],
    queryFn: () => fetch("/healthz", { credentials: "include" }).then((r) => r.json() as Promise<{ status: string; version: string }>),
    staleTime: 60_000,
  });

  const apiBase = (import.meta.env.VITE_API_BASE ?? t("settings.about.apiProxy")) as string;

  return (
    <div className="bg-admin-bg2 border border-admin-border rounded-lg p-6 text-sm space-y-2">
      <Row label={t("settings.about.buildMode")} value={import.meta.env.MODE} />
      <Row label={t("settings.about.backendVersion")} value={health?.version ?? "—"} />
      <Row
        label={t("settings.about.backendStatus")}
        value={health?.status === "ok" ? t("settings.about.statusOk") : t("settings.about.statusDown")}
      />
      <Row label={t("settings.about.apiBase")} value={apiBase} />
      <Row label={t("settings.about.supportLabel")} value={t("settings.about.supportValue")} />
    </div>
  );
}

// ─── Shared sub-components ───────────────────────────────────────────────

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-4">
      <span className="text-admin-text3 w-24">{label}</span>
      <span className="text-admin-text1">{value}</span>
    </div>
  );
}

// ─── Alert rules editor ──────────────────────────────────────────────────

const METRICS: AlertRuleInput["metric"][] = [
  "hr", "spo2", "sbp", "dbp", "rr", "hrv_rmssd", "hrv_sdnn", "stress", "wellness",
];
const COMPARATORS: AlertRuleInput["comparator"][] = [">", ">=", "<", "<=", "=="];
const SEVERITIES: AlertRuleInput["severity"][] = ["critical", "warning", "info"];

function AlertRulesTab({ t, readOnly }: { t: TFunction; readOnly: boolean }) {
  const qc = useQueryClient();
  const rulesQ = useQuery({
    queryKey: ["alert-rules"],
    queryFn: () => alertsApi.rules(),
  });
  const [draft, setDraft] = useState<AlertRuleInput[] | null>(null);

  // Since ADR-032 all rules (including the former built-ins) are editable and
  // returned as one flat list. Drafted locally before PUT.
  const rules: AlertRuleInput[] = draft ?? (rulesQ.data ?? []).map(stripID);

  const saveMut = useMutation({
    mutationFn: (xs: AlertRuleInput[]) => alertsApi.replaceRules(xs),
    onSuccess: (saved) => {
      void qc.invalidateQueries({ queryKey: ["alert-rules"] });
      setDraft(saved.map(stripID));
      toast.success(t("settings.rules.saveSuccess"));
    },
    onError: (err) =>
      toast.error(t("settings.rules.saveFailed"), { description: (err as Error).message }),
  });

  function update(i: number, patch: Partial<AlertRuleInput>) {
    const next = rules.map((r, idx) =>
      idx === i ? ({ ...r, ...patch } as AlertRuleInput) : r,
    );
    setDraft(next);
  }
  function add() {
    setDraft([
      ...rules,
      { metric: "hr", severity: "warning", comparator: ">", threshold: 120 },
    ]);
  }
  function remove(i: number) {
    setDraft(rules.filter((_, idx) => idx !== i));
  }

  return (
    <div className="space-y-4">
      <div className="bg-admin-bg2 border border-admin-border rounded-lg">
        <div className="px-4 py-3 border-b border-admin-border flex items-center justify-between">
          <div className="text-sm">
            <span className="text-admin-text2">{t("settings.rules.customHeader")}</span>
          </div>
          {!readOnly && (
            <button
              type="button"
              onClick={add}
              className="inline-flex items-center gap-1.5 text-sm text-admin-cyan hover:underline"
            >
              <Plus className="w-4 h-4" /> {t("settings.rules.add")}
            </button>
          )}
        </div>
        {readOnly && <ReadOnlyHint t={t} />}

        {rulesQ.isLoading ? (
          <div className="p-6 text-admin-text2 text-sm">{t("settings.rules.loading")}</div>
        ) : rules.length === 0 ? (
          <div className="p-8 text-center text-admin-text3 text-sm">
            {t("settings.rules.empty")}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-admin-bg3 text-admin-text2 text-xs">
              <tr>
                <Th>{t("settings.rules.headers.metric")}</Th>
                <Th>{t("settings.rules.headers.comparator")}</Th>
                <Th>{t("settings.rules.headers.threshold")}</Th>
                <Th>{t("settings.rules.headers.severity")}</Th>
                <Th> </Th>
              </tr>
            </thead>
            <tbody>
              {rules.map((r, i) =>
                readOnly ? (
                  // Read-only: render each rule as plain text — no form controls.
                  <tr key={i} className="border-t border-admin-border text-admin-text1">
                    <td className="px-3 py-2">{t(`trends.metrics.${r.metric}`)}</td>
                    <td className="px-3 py-2">{r.comparator}</td>
                    <td className="px-3 py-2">{r.threshold}</td>
                    <td className="px-3 py-2">{t(`settings.rules.severity.${r.severity}`)}</td>
                    <td className="px-3 py-2" />
                  </tr>
                ) : (
                  <tr key={i} className="border-t border-admin-border">
                    <td className="px-3 py-2">
                      <Select
                        value={r.metric}
                        onChange={(v) => update(i, { metric: v as AlertRuleInput["metric"] })}
                        options={METRICS}
                        renderLabel={(v) => t(`trends.metrics.${v}`)}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <Select value={r.comparator} onChange={(v) => update(i, { comparator: v as AlertRuleInput["comparator"] })} options={COMPARATORS} />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        step="any"
                        value={r.threshold}
                        onChange={(e) => update(i, { threshold: Number(e.target.value) })}
                        className="w-24 bg-admin-bg3 border border-admin-border rounded px-2 py-1 text-admin-text1 focus:outline-none focus:border-admin-cyan"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <Select
                        value={r.severity}
                        onChange={(v) => update(i, { severity: v as AlertRuleInput["severity"] })}
                        options={SEVERITIES}
                        renderLabel={(v) => t(`settings.rules.severity.${v}`)}
                      />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => remove(i)}
                        className="inline-flex items-center text-admin-red hover:text-admin-red/80"
                        aria-label={t("settings.rules.deleteAria")}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        )}
      </div>

      {!readOnly && (
        <div className="flex justify-end gap-2">
          <button
            type="button"
            disabled={draft === null}
            onClick={() => setDraft(null)}
            className="px-3 py-1.5 text-sm border border-admin-border rounded text-admin-text2 hover:bg-admin-bg3 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {t("settings.rules.reset")}
          </button>
          <button
            type="button"
            disabled={draft === null || saveMut.isPending}
            onClick={() => draft && saveMut.mutate(draft)}
            className="px-3 py-1.5 text-sm bg-admin-cyan text-admin-bg rounded hover:bg-admin-cyan/90 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saveMut.isPending ? t("settings.rules.saving") : t("settings.rules.save")}
          </button>
        </div>
      )}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="text-left px-3 py-2 uppercase tracking-wider">{children}</th>
  );
}

function Select<T extends string>({
  value,
  onChange,
  options,
  renderLabel,
}: {
  value: T;
  onChange: (v: T) => void;
  options: T[];
  renderLabel?: (v: T) => string;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as T)}
      className="bg-admin-bg3 border border-admin-border rounded px-2 py-1 text-admin-text1 focus:outline-none focus:border-admin-cyan"
    >
      {options.map((o) => (
        <option key={o} value={o}>
          {renderLabel ? renderLabel(o) : o}
        </option>
      ))}
    </select>
  );
}

// Reduce a server rule view (which carries id / is_active) down to the editable
// AlertRuleInput shape used by the draft + PUT.
function stripID(r: AlertRuleView | AlertRule): AlertRuleInput {
  return {
    metric: r.metric,
    severity: r.severity,
    comparator: r.comparator,
    threshold: r.threshold,
  };
}
